import { config } from '../config';

export interface User {
  authServiceUuid: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  primaryPhoneNumber?: string;
  secondaryPhoneNumber?: string;
  source: string;
  datacenters: string[];
  created: string;
  updated: string;
  lastLogin?: string;
  emailVerifiedDate?: string;
  isAccountLocked: boolean;
  passwordUpdatedOnDate?: string;
  isSource: boolean;
  needsPasswordReset: boolean;
  hasPassword: boolean;
  forgottenOn?: string;
}

interface JsonApiArrayResponse<T> {
  data: T[];
  errors?: string[];
  jsonapi?: {
    version: string;
  };
}

/**
 * Fetch user details by UUID
 * @param uuid User UUID
 * @param token Authorization token
 * @param signal Abort signal to cancel the request
 * @returns User data
 */
export async function fetchUserByUuid(uuid: string, token: string, signal?: AbortSignal): Promise<User> {
  const response = await fetch(`${config.GATEWAY_URL}/find/api/users/${uuid}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch user details by a list of authServiceUuids
 * @param authServiceUuids User UUIDs
 * @param token Authorization token
 * @param signal Optional abort signal for request cancellation
 * @returns Array of user data
 */
export async function fetchUsersByAuthServiceUuids(
  authServiceUuids: string[],
  token: string,
  signal?: AbortSignal,
): Promise<User[]> {
  if (authServiceUuids.length === 0) {
    return [];
  }

  const queryValue: string = encodeURIComponent(authServiceUuids.join(','));
  const response: Response = await fetch(`${config.GATEWAY_URL}/api/users?authServiceUuids=${queryValue}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }

  const payload: JsonApiArrayResponse<User> = await response.json();
  return Array.isArray(payload.data) ? payload.data : [];
}
